import { useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { PageHeader } from '../../components/ui/Card'
import { Badge } from '../../components/ui/Badge'
import { Button } from '../../components/ui/Button'
import { Select } from '../../components/ui/Input'
import { Spinner } from '../../components/ui/Spinner'
import { PermissionGate } from '../../components/PermissionGate'
import { useAuth } from '../../auth/AuthContext'
import { projectsApi } from '../../api/projects.api'
import { tasksApi } from '../../api/tasks.api'
import { labelsApi } from '../../api/labels.api'
import { usersApi } from '../../api/users.api'
import { toast } from '../../store/toastStore'
import { KanbanBoard } from '../tasks/board/KanbanBoard'
import { TaskListView } from '../tasks/shared/TaskListView'
import { TaskCalendarView } from '../tasks/shared/TaskCalendarView'
import { TaskFormModal } from '../tasks/shared/TaskFormModal'
import { TaskDetailModal } from '../tasks/detail/TaskDetailModal'
import { PRIORITY_OPTIONS } from '../tasks/shared/PriorityBadge'
import { ProjectFormModal } from './ProjectFormModal'
import { ProjectTimeTracking } from './ProjectTimeTracking'
import { ProjectSettingsPanel } from './ProjectSettingsPanel'
import { DocumentationTab } from '../documentation/DocumentationTab'
import { RoadmapTab } from '../roadmap/RoadmapTab'
import { AutomationTab } from '../automation/AutomationTab'
import { BrainstormingTab } from '../brainstorming/BrainstormingTab'
import type { ProjectStatus, TaskPriority } from '../../api/types'
import styles from './ProjectDetail.module.css'

const PROJECT_STATUS_LABELS: Record<ProjectStatus, string> = {
  PLANNING: 'Planung',
  ACTIVE: 'Aktiv',
  PAUSED: 'Pausiert',
  COMPLETED: 'Abgeschlossen',
  ARCHIVED: 'Archiviert',
}

type ViewTab = 'board' | 'list' | 'calendar' | 'docs' | 'roadmap' | 'brainstorming' | 'automation' | 'settings'

const TAB_LABELS: Record<ViewTab, string> = {
  board: 'Board',
  list: 'Liste',
  calendar: 'Kalender',
  docs: 'Dokumentation',
  roadmap: 'Roadmap',
  brainstorming: 'Brainstorming',
  automation: 'Automation',
  settings: 'Einstellungen',
}

export function ProjectDetailPage() {
  const { id } = useParams<{ id: string }>()
  const projectId = id!
  const queryClient = useQueryClient()
  const { hasPermission } = useAuth()

  const [tab, setTab] = useState<ViewTab>('board')
  const [editProject, setEditProject] = useState(false)
  const [showTaskForm, setShowTaskForm] = useState<string | null>(null) // holds initialStatusId or 'new'
  const [openTaskId, setOpenTaskId] = useState<string | null>(null)
  const [filterAssignee, setFilterAssignee] = useState('')
  const [filterLabel, setFilterLabel] = useState('')
  const [filterPriority, setFilterPriority] = useState<TaskPriority | ''>('')

  const projectQuery = useQuery({ queryKey: ['projects', projectId], queryFn: async () => (await projectsApi.get(projectId)).data })
  const usersQuery = useQuery({ queryKey: ['users', { pageSize: 100 }], queryFn: async () => (await usersApi.list({ pageSize: 100 })).data })
  const labelsQuery = useQuery({ queryKey: ['labels', projectId], queryFn: async () => (await labelsApi.list(projectId)).data })

  const tasksQuery = useQuery({
    queryKey: ['tasks', { projectId, assigneeUserId: filterAssignee, labelId: filterLabel, priority: filterPriority }],
    queryFn: async () =>
      (
        await tasksApi.list({
          projectId,
          topLevelOnly: true,
          pageSize: 500,
          assigneeUserId: filterAssignee || undefined,
          labelId: filterLabel || undefined,
          priority: filterPriority || undefined,
        })
      ).data,
    enabled: Boolean(projectId),
  })

  const moveMutation = useMutation({
    mutationFn: (input: { taskId: string; statusId: string }) => tasksApi.move(input.taskId, input.statusId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['tasks'], exact: false }),
    onError: () => toast.error('Status konnte nicht geändert werden.'),
  })

  const project = projectQuery.data
  const tasks = useMemo(() => tasksQuery.data ?? [], [tasksQuery.data])

  if (!project) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-6)' }}>
        <Spinner size={28} />
      </div>
    )
  }

  return (
    <div>
      <div className={styles.header}>
        <PageHeader
          title={project.name}
          subtitle={project.description || undefined}
          actions={
            <PermissionGate anyOf={['projects.edit']}>
              <Button variant="secondary" onClick={() => setEditProject(true)}>
                Bearbeiten
              </Button>
            </PermissionGate>
          }
        />
        <div className={styles.titleRow}>
          <Badge>{PROJECT_STATUS_LABELS[project.status]}</Badge>
          <span style={{ color: 'var(--text-muted)', fontSize: 'var(--text-sm)' }}>{project._count.tasks} Aufgaben</span>
          {project.targetDate ? (
            <span style={{ color: 'var(--text-muted)', fontSize: 'var(--text-sm)' }}>
              Ziel: {new Date(project.targetDate).toLocaleDateString('de-DE')}
            </span>
          ) : null}
        </div>
        <div className={styles.progressBar}>
          <div className={styles.progressFill} style={{ width: `${project.progress}%` }} />
        </div>
      </div>

      <ProjectTimeTracking projectId={projectId} />

      <div className={styles.tabs}>
        {(['board', 'list', 'calendar', 'docs', 'roadmap', 'brainstorming', 'automation', 'settings'] as ViewTab[])
          .filter((t) => {
            if (t === 'docs') return hasPermission('documentation.view')
            if (t === 'roadmap') return hasPermission('roadmap.view')
            if (t === 'brainstorming') return hasPermission('brainstorming.view')
            if (t === 'automation') return hasPermission('automation.view')
            return true
          })
          .map((t) => (
            <button key={t} className={[styles.tab, tab === t ? styles.tabActive : ''].join(' ')} onClick={() => setTab(t)}>
              {TAB_LABELS[t]}
            </button>
          ))}
      </div>

      {tab === 'board' || tab === 'list' || tab === 'calendar' ? (
        <div className={styles.filterBar}>
          <Select className={styles.filterSelect} value={filterAssignee} onChange={(e) => setFilterAssignee(e.target.value)}>
            <option value="">Alle Verantwortlichen</option>
            {usersQuery.data?.map((u) => (
              <option key={u.id} value={u.id}>
                {u.displayName}
              </option>
            ))}
          </Select>
          <Select className={styles.filterSelect} value={filterLabel} onChange={(e) => setFilterLabel(e.target.value)}>
            <option value="">Alle Labels</option>
            {labelsQuery.data?.map((l) => (
              <option key={l.id} value={l.id}>
                {l.name}
              </option>
            ))}
          </Select>
          <Select className={styles.filterSelect} value={filterPriority} onChange={(e) => setFilterPriority(e.target.value as TaskPriority | '')}>
            <option value="">Alle Prioritäten</option>
            {PRIORITY_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>
          <PermissionGate anyOf={['tasks.create']}>
            <Button size="sm" variant="primary" onClick={() => setShowTaskForm('new')}>
              + Aufgabe
            </Button>
          </PermissionGate>
        </div>
      ) : null}

      {tab === 'board' ? (
        <KanbanBoard
          statuses={project.taskStatuses}
          tasks={tasks}
          onOpenTask={setOpenTaskId}
          onMoveTask={(taskId, statusId) => moveMutation.mutate({ taskId, statusId })}
          onCreateInStatus={setShowTaskForm}
          canCreate={true}
        />
      ) : null}

      {tab === 'list' ? <TaskListView tasks={tasks} onOpenTask={setOpenTaskId} /> : null}

      {tab === 'calendar' ? <TaskCalendarView tasks={tasks} onOpenTask={setOpenTaskId} /> : null}

      {tab === 'docs' ? <DocumentationTab projectId={projectId} /> : null}

      {tab === 'roadmap' ? <RoadmapTab projectId={projectId} /> : null}

      {tab === 'brainstorming' ? <BrainstormingTab projectId={projectId} /> : null}

      {tab === 'automation' ? <AutomationTab projectId={projectId} /> : null}

      {tab === 'settings' ? <ProjectSettingsPanel project={project} /> : null}

      {editProject ? <ProjectFormModal project={project} onClose={() => setEditProject(false)} /> : null}

      {showTaskForm ? (
        <TaskFormModal
          projectId={projectId}
          initialStatusId={showTaskForm !== 'new' ? showTaskForm : undefined}
          onClose={() => setShowTaskForm(null)}
        />
      ) : null}

      {openTaskId ? <TaskDetailModal taskId={openTaskId} onClose={() => setOpenTaskId(null)} onOpenTask={setOpenTaskId} /> : null}
    </div>
  )
}
